import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Modal,
  Input,
  Select,
  Tag,
  InputNumber,
  Switch,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import {
  getSubscriptionTypes,
  createSubscriptionType,
  updateSubscriptionType,
} from "../../api/subscription_types/subscriptionTypesRequest";
import { getTypeLines } from "../../api/typeLine/typeLineRequest";
import { getZones } from "../../api/zone/zoneRequest";
import { LOCATION_TYPES } from "../../utils/constants";
import PermissionRender from "../../components/PermissionRender";

const { Content } = Layout;
const { Option } = Select;

function SubscriptionTypes() {
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [typeLines, setTypeLines] = useState([]);
  const [zones, setZones] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSubscriptionType, setCurrentSubscriptionType] = useState({
    id: null,
    name: "",
    description: "",
    is_active: true,
    type_line_ids: [],
    location_type: null,
    max_locations: null,
    requires_approval: false,
    uses_zone_prefix: true,
    fixed_zone_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [typeLineLoading, setTypeLineLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    fetchSubscriptionTypes();
    fetchTypeLines();
    fetchZones();
  }, []);

  const fetchSubscriptionTypes = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptionTypes();
      setSubscriptionTypes(data);
    } catch (error) {
      message.error("Échec du chargement des types d'abonnement");
    } finally {
      setLoading(false);
    }
  };
  const fetchTypeLines = async () => {
    setTypeLineLoading(true);
    try {
      const data = await getTypeLines();
      setTypeLines(data);
    } catch (error) {
      message.error("Échec du chargement des types de ligne");
    } finally {
      setTypeLineLoading(false);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const data = await getZones();
      setZones(data.data || data);
    } catch (error) {
      message.error("Échec du chargement des contrats");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriptionType = async () => {
    if (
      currentSubscriptionType.name.trim() &&
      currentSubscriptionType.type_line_ids.length > 0
    ) {
      try {
        if (isEditMode) {
          await updateSubscriptionType(
            currentSubscriptionType.id,
            currentSubscriptionType
          );
          message.success("Type d'abonnement modifié avec succès");
        } else {
          await createSubscriptionType(currentSubscriptionType);
          message.success("Type d'abonnement ajouté avec succès");
        }
        resetForm();
        fetchSubscriptionTypes();
      } catch (error) {
        message.error("Erreur lors de l'enregistrement du type d'abonnement");
      }
    } else {
      message.error(
        "Veuillez remplir tous les champs obligatoires et sélectionner au moins un type de ligne"
      );
    }
  };
  const handleEditSubscriptionType = (subscriptionType) => {
    // Extract type_line_ids from allowedLineTypes
    const type_line_ids =
      subscriptionType.allowed_line_types?.map(
        (lineType) => lineType.type_line_id
      ) || [];

    setCurrentSubscriptionType({
      ...subscriptionType,
      type_line_ids,
      uses_zone_prefix: subscriptionType.uses_zone_prefix ?? true,
      fixed_zone_id: subscriptionType.fixed_zone_id || null,
    });
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const resetForm = () => {
    setCurrentSubscriptionType({
      id: null,
      name: "",
      description: "",
      is_active: true,
      type_line_ids: [],
      location_type: null,
      max_locations: null,
      requires_approval: false,
      uses_zone_prefix: true,
      fixed_zone_id: null,
    });
    setIsEditMode(false);
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Types de Ligne",
      key: "line_types",
      render: (_, record) => (
        <div>
          {record.allowed_line_types && record.allowed_line_types.length > 0
            ? record.allowed_line_types.map((lineType) => (
                <Tag color="blue" key={lineType.id}>
                  {lineType.type_line?.name || "N/A"}
                </Tag>
              ))
            : "Aucun type de ligne"}
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (is_active) => (
        <span className={is_active ? "text-green-600" : "text-red-600"}>
          {is_active ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      title: "Type d'emplacement",
      dataIndex: "location_type",
      key: "location_type",
      render: (location_type) =>
        LOCATION_TYPES[location_type] || "Non spécifié",
    },
    {
      title: "Emplacements max",
      dataIndex: "max_locations",
      key: "max_locations",
      render: (max_locations) => max_locations || "Illimité",
    },
    {
      title: "Approbation requise",
      dataIndex: "requires_approval",
      key: "requires_approval",
      render: (requires_approval) => (
        <span>{requires_approval ? "Oui" : "Non"}</span>
      ),
    },
    {
      title: "Utilise préfixe Contrat",
      dataIndex: "uses_zone_prefix",
      key: "uses_zone_prefix",
      render: (uses_zone_prefix) => (
        <span>{uses_zone_prefix ? "Oui" : "Non"}</span>
      ),
    },
    {
      title: "Contrat fixe",
      dataIndex: "fixed_zone_id",
      key: "fixed_zone_id",
      render: (fixed_zone_id, record) => {
        if (!fixed_zone_id) return "Aucune";
        const zone = zones.find((z) => z.id === fixed_zone_id);
        return zone ? zone.name : `Zone ${fixed_zone_id}`;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Space size="middle">
          <PermissionRender
            requiredPermission={["subscription_type", "update"]}
          >
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditSubscriptionType(record)}
            >
              Modifier
            </Button>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24}>
              <PermissionRender
                requiredPermission={["subscription_type", "create"]}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setIsModalVisible(true);
                  }}
                  className="w-full"
                >
                  Ajouter un type d'abonnement
                </Button>
              </PermissionRender>
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm">
          <Table
            dataSource={subscriptionTypes.map((type) => ({
              key: type.id,
              ...type,
            }))}
            columns={columns}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} types d'abonnement`,
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </Card>

        <Modal
          title={
            isEditMode
              ? "Modifier le type d'abonnement"
              : "Ajouter un type d'abonnement"
          }
          visible={isModalVisible}
          onOk={handleAddSubscriptionType}
          onCancel={resetForm}
          width={600}
        >
          <div className="space-y-4">
            <Input
              placeholder="Nom"
              value={currentSubscriptionType.name}
              onChange={(e) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  name: e.target.value,
                })
              }
            />
            <Input.TextArea
              placeholder="Description"
              value={currentSubscriptionType.description}
              onChange={(e) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  description: e.target.value,
                })
              }
              rows={4}
            />
            <Select
              placeholder="Sélectionner les types de ligne"
              mode="multiple"
              loading={typeLineLoading}
              style={{ width: "100%" }}
              value={currentSubscriptionType.type_line_ids}
              onChange={(values) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  type_line_ids: values,
                })
              }
            >
              {typeLines.map((typeLine) => (
                <Option key={typeLine.id} value={typeLine.id}>
                  {typeLine.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Statut"
              value={currentSubscriptionType.is_active}
              onChange={(value) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  is_active: value,
                })
              }
              style={{ width: "100%" }}
            >
              <Option value={true}>Actif</Option>
              <Option value={false}>Inactif</Option>
            </Select>
            <Select
              placeholder="Type d'emplacement"
              style={{ width: "100%" }}
              value={currentSubscriptionType.location_type}
              onChange={(value) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  location_type: value,
                })
              }
              allowClear
            >
              {Object.entries(LOCATION_TYPES).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value}
                </Option>
              ))}
            </Select>
            <InputNumber
              placeholder="Nombre maximum d'emplacements"
              style={{ width: "100%" }}
              min={1}
              value={currentSubscriptionType.max_locations}
              onChange={(value) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  max_locations: value,
                })
              }
            />{" "}
            <div className="flex items-center justify-between">
              <span>Approbation requise:</span>
              <Switch
                checked={currentSubscriptionType.requires_approval}
                onChange={(checked) =>
                  setCurrentSubscriptionType({
                    ...currentSubscriptionType,
                    requires_approval: checked,
                  })
                }
              />
            </div>{" "}
            <div className="flex items-center justify-between">
              <span>Utilise préfixe de Contrat:</span>
              <Switch
                checked={currentSubscriptionType.uses_zone_prefix}
                onChange={(checked) =>
                  setCurrentSubscriptionType({
                    ...currentSubscriptionType,
                    uses_zone_prefix: checked,
                    fixed_zone_id: checked
                      ? null
                      : currentSubscriptionType.fixed_zone_id,
                  })
                }
              />
            </div>{" "}
            <Select
              placeholder="Contrat fixe (optionnel)"
              style={{ width: "100%" }}
              value={currentSubscriptionType.fixed_zone_id}
              onChange={(value) =>
                setCurrentSubscriptionType({
                  ...currentSubscriptionType,
                  fixed_zone_id: value,
                  uses_zone_prefix: value
                    ? false
                    : currentSubscriptionType.uses_zone_prefix,
                })
              }
              allowClear
              disabled={currentSubscriptionType.uses_zone_prefix}
            >
              {zones.map((zone) => (
                <Option key={zone.id} value={zone.id}>
                  {zone.name} {zone.prefix && `(${zone.prefix})`}
                </Option>
              ))}
            </Select>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}

export default SubscriptionTypes;
